<template>
    <Calendar
        ref="calendar"
        dateFormat="dd/mm/yy"
        :modelValue="modelValue"
        :selectionMode="pickerRange ? 'range' : 'single'"
        :disabled="disabled"
        :showTime="showTime"
        :timeOnly="timeOnly"
        :showIcon="showIcon"
        :minDate="minDate || undefined"
        :maxDate="maxDate || undefined"
        :inputClass="invalid ? 'p-invalid' : ''"
        :panelClass="panelClass"
        :placeholder="placeholderLabel"
        @update:modelValue="onUpdateModelValue"
        @date-select="$emit('date-select', $event)"
        @show="onShow"
        @hide="onHide"
    >
        <template v-if="shortcuts.length" #footer>
            <div class="calendar-form-shortcuts">
                <button v-for="shortcut in shortcuts" :key="shortcut.label" type="button" class="calendar-form-shortcut" :class="{ 'is-selected': isSelected(shortcut.value) }" @click="selectShortcut(shortcut.value)">
                    <span class="calendar-form-shortcut-label">{{ shortcut.label }}</span>
                    <span class="calendar-form-shortcut-period">{{ shortcut.period }}</span>
                </button>
            </div>
        </template>
    </Calendar>
</template>

<script>
import Calendar from 'primevue/calendar'
import moment from 'moment'

export default {
    name: 'CalendarForm',

    components: {
        Calendar,
    },

    props: {
        modelValue: {
            type: [Date, Array, String],
            default: null,
        },

        // true => selectionMode="range" e atalhos de período (hoje, ontem, este mês, mês anterior)
        pickerRange: {
            type: Boolean,
            default: false,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        invalid: {
            type: Boolean,
            default: false,
        },

        showTime: {
            type: Boolean,
            default: false,
        },

        timeOnly: {
            type: Boolean,
            default: false,
        },

        showIcon: {
            type: Boolean,
            default: true,
        },

        minDate: {
            type: [Date, Boolean],
            default: false,
        },

        maxDate: {
            type: [Date, Boolean],
            default: false,
        },

        placeholder: {
            type: String,
            default: '',
        },
    },

    emits: ['update:modelValue', 'date-select'],

    data: () => ({
        // referência dos atalhos, renovada a cada abertura: a tela fica aberta por horas e pode virar o dia
        reference: new Date(),
    }),

    computed: {
        placeholderLabel() {
            if (this.placeholder) return this.placeholder

            if (this.pickerRange) return 'Selecione o período'

            if (this.timeOnly) return 'Selecione a hora'

            return 'Selecione a data'
        },

        // o Calendar declara panelClass como String: array/objeto aqui vira warning de prop
        panelClass() {
            return this.pickerRange ? 'calendar-form-panel' : 'calendar-form-panel calendar-form-panel-single'
        },

        shortcuts() {
            if (this.showTime || this.timeOnly) return []

            const today = moment(this.reference)
            const yesterday = today.clone().subtract(1, 'day')
            const lastMonth = today.clone().subtract(1, 'month')

            if (!this.pickerRange) {
                return [this.buildShortcut('Hoje', today), this.buildShortcut('Ontem', yesterday)]
            }

            return [
                this.buildShortcut('Hoje', today, today),
                this.buildShortcut('Ontem', yesterday, yesterday),
                this.buildShortcut('Este Mês', today.clone().startOf('month'), today.clone().endOf('month')),
                this.buildShortcut('Mês anterior', lastMonth.clone().startOf('month'), lastMonth.clone().endOf('month')),
            ]
        },
    },

    methods: {
        buildShortcut(label, begin, end) {
            if (!end) return { label, value: begin.toDate(), period: begin.format('DD/MM') }

            const limitedEnd = this.limitToMaxDate(end)

            return { label, value: [begin.toDate(), limitedEnd.toDate()], period: `${begin.format('DD/MM')} - ${limitedEnd.format('DD/MM')}` }
        },

        // o fim do mês corrente passa do maxDate das telas de filtro (hoje); sem o corte o card
        // anunciaria um período que o próprio calendário não deixa selecionar
        limitToMaxDate(date) {
            if (!this.maxDate) return date

            const max = moment(this.maxDate)

            return date.isAfter(max, 'day') ? max : date
        },

        selectShortcut(value) {
            this.$refs.calendar.overlayVisible = false

            this.$emit('update:modelValue', value)
        },

        onUpdateModelValue(value) {
            this.$emit('update:modelValue', value)

            // o Calendar 3.10 não tem hideOnRangeSelection: fecha o painel quando o intervalo fecha
            if (this.pickerRange && Array.isArray(value) && value[0] && value[1]) {
                this.$refs.calendar.overlayVisible = false
            }
        },

        onShow() {
            this.reference = new Date()
        },

        onHide() {
            // intervalo deixado pela metade (só a data inicial) vale como período de um dia
            if (this.pickerRange && Array.isArray(this.modelValue) && this.modelValue[0] && !this.modelValue[1]) {
                this.$emit('update:modelValue', [this.modelValue[0], this.modelValue[0]])
            }
        },

        isSelected(value) {
            if (!this.modelValue) return false

            if (!this.pickerRange) return moment(this.modelValue).isSame(value, 'day')

            if (!Array.isArray(this.modelValue) || !this.modelValue[0] || !this.modelValue[1]) return false

            return moment(this.modelValue[0]).isSame(value[0], 'day') && moment(this.modelValue[1]).isSame(value[1], 'day')
        },
    },
}
</script>

<!-- o painel é teleportado para o body: estilo global, prefixado para não vazar em outros datepickers -->
<style lang="scss">
.calendar-form-panel {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
}

/* Ao abrir, o Calendar congela a largura medida do painel em style.width, arredondada para
 * pixel inteiro (alignOverlay). Se o conteúdo real pedir um punhado de pixels a mais do que
 * essa medida, o flex-wrap joga a coluna de atalhos para baixo do calendário. O max-content
 * garante a largura do conteúdo; o !important existe porque o Calendar também escreve
 * min-width inline (a largura do input), e estilo inline vence folha externa. */
.p-datepicker.calendar-form-panel {
    min-width: max-content !important;
}

/* Calendário compacto. O tema entrega célula de 49px (span de 2.5rem + 0.5rem de recuo), o que
 * deixa o painel bem mais alto que a coluna de atalhos ao lado. O prefixo dobrado
 * (.p-datepicker.calendar-form-panel) existe porque o tema mira as medidas com a mesma
 * especificidade — sem ele, quem vence depende da ordem de carregamento das folhas. */
.p-datepicker.calendar-form-panel {
    .p-datepicker-header {
        padding: 0.25rem;
    }

    .p-datepicker-header .p-datepicker-prev,
    .p-datepicker-header .p-datepicker-next {
        width: 1.75rem;
        height: 1.75rem;
    }

    .p-datepicker-header .p-datepicker-title {
        line-height: 1.75rem;
    }

    .p-datepicker-header .p-datepicker-title .p-datepicker-month,
    .p-datepicker-header .p-datepicker-title .p-datepicker-year {
        padding: 0.25rem;
    }

    table {
        margin: 0.25rem 0 0;
        font-size: 0.85rem;
    }

    table th,
    table td {
        padding: 0.1rem;
    }

    table th > span,
    table td > span {
        width: 2rem;
        height: 2rem;
    }
}

.calendar-form-panel.calendar-form-panel-single {
    display: block;
}

/* se a página pedir showButtonBar, ela cai numa linha própria abaixo do calendário */
.calendar-form-panel .p-datepicker-buttonbar {
    order: 3;
    flex: 0 0 100%;
}

.calendar-form-shortcuts {
    display: flex;
    /* acompanha a altura do calendário ao lado; os cards dividem a sobra entre si */
    align-self: stretch;
    flex-direction: column;
    gap: 0.5rem;
    margin-left: 0.5rem;
    padding: 0.25rem 0 0.25rem 0.75rem;
    border-left: 1px solid var(--surface-border);
}

.calendar-form-panel-single .calendar-form-shortcuts {
    flex-direction: row;
    margin-left: 0;
    padding: 0.75rem 0 0.25rem;
    border-left: 0;
    border-top: 1px solid var(--surface-border);
}

.calendar-form-shortcut {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.15rem;
    min-width: 8.5rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid transparent;
    border-radius: 8px;
    background: var(--surface-50);
    color: var(--text-color);
    font-family: inherit;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s, color 0.2s;
}

.calendar-form-shortcut:hover {
    background: var(--surface-100);
}

.calendar-form-shortcut.is-selected {
    background: var(--primary-lighter-color);
    border-color: var(--primary-color);
    color: var(--primary-color);
}

.calendar-form-shortcut-label {
    font-size: 0.85rem;
    font-weight: 600;
}

.calendar-form-shortcut-period {
    font-size: 0.75rem;
    color: var(--text-color-secondary);
}

.calendar-form-shortcut.is-selected .calendar-form-shortcut-period {
    color: inherit;
}

@media screen and (max-width: 575px) {
    .calendar-form-panel {
        flex-direction: column;
    }

    .calendar-form-shortcuts {
        flex-direction: row;
        flex-wrap: wrap;
        margin-left: 0;
        padding: 0.75rem 0 0.25rem;
        border-left: 0;
        border-top: 1px solid var(--surface-border);
    }
}
</style>
